/*
 * CREATE-BUDGET.JSX - Create Budget Component with Global User Permissions
 * 
 * 
 * SUMMARY:
 * Budget creation component that manages project budget display and editing with
 * enhanced permission controls for global users during quote processing states.
 * Handles budget calculations, task interactions, and financial reporting.
 */
import React, { Fragment } from "react";
import { connect } from "react-redux";
import { Route, Routes } from "react-router-dom";
import { bindActionCreators } from "redux";
import _ from "lodash";
import idx from "idx";
import { resolveProjectType } from "../../utils/resolve-project-type";
import {
  PROJECT_MANAGEMENT,
  PROJECT_BUDGET_COST_BREAKDOWN,
} from "../../../../constants/route-paths";
import { warningSnackBar } from "../../../../actions/common.action";
import { getLaborCost, getStageAttributedPoActuals } from "../../../../actions/dashboard.action";
import {
  getProject,
  getOptions,
  getProgressClaimList,
  getProjectSummary,
} from "../../../../actions/project-management.action";
import {
  getCategories,
  getItems,
  getLevel,
} from "../../../../actions/inventory.action";
import AddTask from "../../add-task";
import BudgetRetention from "./budget-retention";
import PreviewBudget from "./preview-budget";
import CostBreakdown from "./cost-breakdown/index";
import AllocateTimesheetTask from "../../../timesheet/allocate-timesheet-task";
import { numberFormat, toUSD, toPercent, getClaimProfitPct, getClaimProfitAmount } from "../../../../util/currency-formatter";
import "./budget.scss";
import { withRouter } from "../../../../util/with-router";
import Button from 'react-bootstrap/Button';
import { toDecimal, roundValue } from "../../../../util/decimal-utils";
import Table from 'react-bootstrap/Table';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import '../../scss/task.scss';
import StageAccordionBudget from "../../../../components/stage-accordion/budget";
import { Card, CardBody, CardHeader } from "react-bootstrap";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from 'react-bootstrap/Collapse';
import { calculatVariationsTotalsBudget, calculateVariationsBudget } from "../../../../components/stage-accordion/data/project-task-list";
import { calculateActualBudget as calculateActualBudgetData, getProjectSummaryData } from '../../utils/get-project-summary';
import { addProgressClaimsAction } from "../../../../slice/project-summary";
import ProgressClaimRetention from "../../../../components/progressclaim-retention";
import ReceiveCreditInvoiceBudget from "../../../../components/receive-credit-invoice-budget";
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddVariation from "../../add-variation";
/**
 * 
 * DESCRIPTION:
 * This component handles both the UI rendering and business logic for budget 
 * calculations, including gross profit, forecast/actual cost comparison, and 
 * unallocated labor cost management.It supports:
 * 
 * - Viewing original and revised contract values
 * - Displaying stages and variations (with and without sections)
 * - Editing tasks, subtasks, and variations
 * - Calculating forecast vs actual costs and profit/loss
 * - Allocating unassigned timesheet hours to tasks
 * - Generating invoices (Progress Claims)
 * - Navigating to task-specific cost breakdowns
 * 
 * The component integrates multiple Redux actions to fetch data such as:
 * - Project summary and stages
 * - Approved variations
 * - Progress claims
 * - Labor costs and level settings
 * 
 * ROUTES HANDLED:
 * - /budget/:projectId
 * - /budget/:projectId/create
 * - /budget/:projectId/cost-breakdown/:costType/:taskId
 * 
 */

class CreateBudget extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedTask: [],
      // categories: [],
      items: [],
      projectId: "",
      optionData: [],
      taskDialogState: false,
      updatePermission: this.props.permissions.updatePermission,
      taskData: {},
      stageId: "",
      taskId: "",
      budgetRetentionDialog: false,
      projectCalculations: {},
      projectSummary: {
        contractedPrice: 0,
        reviedContractedPrice: 0,
        actualCost: 0,
      },
      projectTypeStatus: "",
      projectQuoteType: "",
      laborCost: {},
      laborCostByStageId: {},
      materialActualByStageId: {},
      subcontractorActualByStageId: {},
      resourceAllocations: [],
      unAllocatedTimesheetTime: 0,
      allocateTimesheetTaskDialog: false,
      stages: [],
      calculations: {},
      progressClaims: [],
      projectCreditTotal: 0,
      variationsWithoutSection: [],
      open: {}
    };
  }

  /**
   * Determines if the active route is a sub-route (e.g., generate invoice or cost breakdown).
   *
   * @returns {boolean} True if the current route is a sub-route, false otherwise.
   */
  isSubRoute = () => {
    const pathname = idx(this.props, _ => _.location.pathname) || '';
    return pathname.endsWith('/create') || pathname.includes('/cost-breakdown');
  };

  componentDidMount() {
    if (!this.isSubRoute()) {
      this.getProject();
    }
  }

  componentDidUpdate(prevProps) {
    const prevPathname = idx(prevProps, _ => _.location.pathname) || '';
    const currentPathname = idx(this.props, _ => _.location.pathname) || '';
    if (prevPathname !== currentPathname && !this.isSubRoute()) {
      if (!this.state.stages || !this.state.stages.length) {
        this.getProject();
      }
    }
  }

  getBudgetClass = (difference) => {
    if (difference > 0) return 'budget-under';
    if (difference < 0) return 'budget-exceed';
    return '';
  }

  submitData = async () => {
    await this.getProject(true);
    this.setState({ allocateTimesheetTaskDialog: false });
  };

  /**
   * Fetches and prepares project data, stages, options, labor costs, and progress claims.
   *
   * @param {boolean} [forceFresh=false] - Whether to bypass Redux cache and force fresh data fetch.
   * @returns {Promise<void>} Resolves when state has been populated.
   */
  getProject = async (forceFresh = false) => {
    const isForceFresh = typeof forceFresh === 'boolean' ? forceFresh : false;
    const projectId = this.props.params.projectId;
    let projectQuoteType = "";
    let optionData = "", projectData = {}, selectedTask = [], project = {};
    if (projectId) {
      const isCurrentProjectInRedux =
        !isForceFresh &&
        idx(this.props, _ => _.projectSummary.project.projectId) === projectId &&
        Array.isArray(idx(this.props, _ => _.projectSummary.sectionData)) &&
        idx(this.props, _ => _.projectSummary.sectionData.length) > 0;

      let summaryResult = null;
      if (isCurrentProjectInRedux) {
        const stages = this.props.projectSummary.sectionData;
        project = _.cloneDeep({ ...(this.props.projectSummary.project.res || {}), stages });
      } else {
        summaryResult = await getProjectSummaryData({ projectId, dispatch: this.props.dispatch, type: 'budget', forceFresh: isForceFresh });
        if (idx(this.props.projectSummary, _ => _.project.res)) {
          const stages = idx(this.props.projectSummary, _ => _.sectionData) || [];
          project = _.cloneDeep({ ...(idx(this.props.projectSummary, _ => _.project.res) || {}), stages });
        } else if (summaryResult?.projectData) {
          project = _.cloneDeep(summaryResult.projectData);
        } else {
          project = _.cloneDeep(await this.props.getProjectSummary(projectId));
        }
      }

      let activeLaborCost = {};
      if (summaryResult) {
        optionData = _.cloneDeep(summaryResult.optionList || []);
        if (summaryResult.laborData) {
          activeLaborCost = summaryResult.laborData.taskData || {};
          const laborCost = activeLaborCost;
          const laborCostByStageId = summaryResult.laborData.stageData || {};
          const unAllocatedTimesheetTime = idx(laborCost, (_) => _.null) || 0;
          this.setState({ laborCost, laborCostByStageId, unAllocatedTimesheetTime });
        }
        if (summaryResult.progressClaims) {
          this.setState({ progressClaims: summaryResult.progressClaims });
        }
      } else if (
        isCurrentProjectInRedux &&
        Array.isArray(this.props.projectSummary?.optionsList) &&
        this.props.projectSummary?.laborData &&
        Array.isArray(this.props.projectSummary?.progressClaims)
      ) {
        optionData = _.cloneDeep(this.props.projectSummary.optionsList || []);
        activeLaborCost = this.props.projectSummary.laborData?.taskData || {};
        const laborCost = activeLaborCost;
        const laborCostByStageId = this.props.projectSummary.laborData?.stageData || {};
        const unAllocatedTimesheetTime = idx(laborCost, (_) => _.null) || 0;
        this.setState({
          laborCost,
          laborCostByStageId,
          unAllocatedTimesheetTime,
          progressClaims: this.props.projectSummary.progressClaims || []
        });
      } else {
        const [opt, laborDataRes, claimsRes] = await Promise.all([
          this.props.getOptions(projectId, { type: 'budget' }),
          this.props.getLaborCost({ id: projectId, skipDashboardData: true }),
          this.props.getProgressClaimList(projectId)
        ]);
        optionData = _.cloneDeep(opt || []);
        activeLaborCost = laborDataRes?.taskData || {};
        const laborCost = activeLaborCost;
        const laborCostByStageId = laborDataRes?.stageData || {};
        const unAllocatedTimesheetTime = idx(laborCost, (_) => _.null) || 0;
        this.setState({
          laborCost,
          laborCostByStageId,
          unAllocatedTimesheetTime,
          progressClaims: Array.isArray(claimsRes) ? claimsRes : []
        });
      }
      await this.getStageAttributedPoActuals(projectId);
      const labors = this.props.projectSummary?.labors || [];
      if (labors.length) {
        this.setState({ labors });
      } else if (project.level) {
        await this.getLevels(project.level);
      }
      const projectCreditTotal = idx(this.props.projectSummary, _ => _.project.res.creditInvoiceTotal) || idx(project, _ => _.creditInvoiceTotal) || 0;
      this.setState({ projectCreditTotal });
      const Stages = project.stages;

      projectData = {
        projectId: project.id,
        projectName: project.name,
        projectDescription: project.description,
        customerName: project.customer && project.customer.businessName,
        hasStages: project.hasStages,
        projectType: project.projectType,
        projectTypeStatus: project.type,//project or tender
        customerLevel: project.customer ? project.customer.level : "",
        levelId: project.level,
        quoteType: project.quoteType,
        projectStatus: project.status,
        Stages: Stages,
      };

      projectQuoteType = project.quoteType;
      this.setState({ projectQuoteType });

      if (Stages && Array.isArray(Stages)) {
        const traverseStages = (stages) => {
          for (const stage of stages) {
            if (Array.isArray(stage.stages)) {
              traverseStages(stage.stages);
            }
            if (stage && Array.isArray(stage.tasks)) {
              stage.tasks.forEach((task) => {
                task.allocatedMaterialCost = idx(task, (_task) => task.materialTotal_SaleAmount) || 0;
                task.allocatedLaborCost = idx(task, (_task) => task.laborTotal_SaleAmount) || 0;
                task.allocatedSubContractorCost = idx(task, (_task) => task.subcontractorTotal_SaleAmount) || 0;
                task.allocatedProfitMarginCost = idx(task, (_task) => task.profitMarginCost) || 0;
                selectedTask.push({ ...task, type: "TASK", stageName: stage.name });
              });
            }
          }
        };
        traverseStages(Stages);
      }
      let processedOptions = [];
      const safeOptionData = _.cloneDeep(optionData || []);
      safeOptionData.forEach((option, optionIndex) => {
        const currentLaborCost = Object.keys(activeLaborCost).length ? activeLaborCost : this.state.laborCost;
        const { actualMaterialCost, actualLaborCost, actualSubContractorCost } = this.calculateActualBudget(option, currentLaborCost);
        option.actualMaterialCost = actualMaterialCost;
        option.actualLaborCost = actualLaborCost;
        option.actualSubContractorCost = actualSubContractorCost;
        option.revisionNumber = idx(option, _ => _.taskRevision[0].RevisionNumber) || '';
        option.revisionId = idx(option, _ => _.taskRevision[0].RevisionId) || '';
        option.revisionName = idx(option, _ => _.taskRevision[0].revisionName) || '';
        option.subTasks = option.subTasks ? option.subTasks : [];
        option.variationNo = this.generateVariationNumber(optionIndex + 1, 3);
        const actualTotal = toDecimal(option.actualMaterialCost || 0)
          .plus(toDecimal(option.actualLaborCost || 0))
          .plus(toDecimal(option.actualSubContractorCost || 0))
          .toNumber();
        const isRejectedOrCancelled = ['REJECTED', 'CANCELLED'].includes(option.proposal_status);
        const shouldShow = !isRejectedOrCancelled && (option.proposal_status === 'APPROVED' || actualTotal > 0);
        if (shouldShow) {
          option.calculatedProfitMargin = option.profitMarginCost;
          selectedTask.push({ ...option, type: "VARIATION", stageId: option.stageId });
          processedOptions.push(option);
        }
      });

      const sortedOptions = _.chain(processedOptions).groupBy("revisionId")
        .map((revisions, revisionId) => {
          const sortedRevisions = _.sortBy(revisions, [(o) => new Date(o.createdAt || null)]) || [];
          return { revisionId, revisions: sortedRevisions };
        })
        .sortBy((o) => o.revisions[0].createdAt)
        .map((data) => data.revisions)
        .value()
        .flat(1);
      const variationsWithoutSection = processedOptions.filter(option => option.stageId === null);
      this.setState({
        ...projectData,
        stages: Stages,
        optionData: sortedOptions,
        selectedTask,
        variationsWithoutSection,
      }, () => this.projectCalculation(Stages, sortedOptions));
    }
  };

  getLaborCost = async (projectId) => {
    const laborData = await this.props.getLaborCost({ id: projectId });
    const laborCost = laborData.taskData || {};
    const laborCostByStageId = laborData.stageData || {};
    const unAllocatedTimesheetTime = idx(laborCost, (_) => _.null) || 0;
    this.setState({ laborCost, laborCostByStageId, unAllocatedTimesheetTime });
  };

  /**
   * GET STAGE ATTRIBUTED PO ACTUALS
   * @Modified 2026-01-20
   *
   * Fetches stage-attributed PurchaseOrder/WorkOrder Actuals keyed by `stageId`.
   * This is used for attribution-flagged section breakdowns on Budget.
   *
   * @param {string} projectId - Project UUID
   * @returns {Promise<void>}
   */
  getStageAttributedPoActuals = async (projectId) => {
    const res = await this.props.getStageAttributedPoActuals({ id: projectId });
    const materialActualByStageId = res?.materialStageData || {};
    const subcontractorActualByStageId = res?.subcontractorStageData || {};
    const materialCommittedByStageId = res?.materialCommittedStageData || {};
    const subcontractorCommittedByStageId = res?.subcontractorCommittedStageData || {};
    const totalCommittedByStageId = res?.totalCommittedStageData || {};
    const committedByTaskId = res?.taskCommittedData || {};
    const taskMaterialCommittedData = res?.taskMaterialCommittedData || {};
    const taskSubcontractorCommittedData = res?.taskSubcontractorCommittedData || {};
    this.setState({
      materialActualByStageId,
      subcontractorActualByStageId,
      materialCommittedByStageId,
      subcontractorCommittedByStageId,
      totalCommittedByStageId,
      committedByTaskId,
      taskMaterialCommittedData,
      taskSubcontractorCommittedData,
    });
  };

  getProgressClaimList = async (projectId) => {
    const progressClaims = await this.props.getProgressClaimList(projectId);
    if (Array.isArray(progressClaims)) {
      this.props.dispatch(addProgressClaimsAction(progressClaims));
    }
    this.setState({ progressClaims });
  };

  calculateActualBudget = (task = {}, laborCost) => {
    let actualProfitMarginCost = 0, profitMarginCost = 0;
    const { actualLaborCost, actualMaterialCost, actualSubContractorCost } = calculateActualBudgetData(task, laborCost);

    actualProfitMarginCost += parseFloat(task.profitMarginCost || 0);
    profitMarginCost += parseFloat(task.profitMarginCost || 0);

    return {
      actualMaterialCost,
      actualLaborCost,
      actualSubContractorCost,
      actualProfitMarginCost,
      profitMarginCost,
    };
  };

  /**
   * Formats the profit/loss dollar amount and profit percentage for display in the summary table.
   * Compares Total Claim Amount vs Actual Cost
   *
   * @param {number|string|Decimal} totalClaimAmount - Total claimed amount (revenue)
   * @param {number|string|Decimal} actualCost - Project actual cost to date
   * @param {number|string|Decimal} unAllocatedTime - Unallocated timesheet time cost
   * @returns {JSX.Element} Stacked dollar value and profit percentage or (-)
   */
  getProfitLossDisplay = (totalClaimAmount, actualCost, unAllocatedTime) => {
    const claimDec = toDecimal(totalClaimAmount);
    const actualCostToDate = toDecimal(actualCost).plus(toDecimal(unAllocatedTime));
    const profitLossValue = claimDec.minus(actualCostToDate);
    const pctDisplay = claimDec.isZero()
      ? "(-)"
      : `(${toPercent(profitLossValue, actualCostToDate)})`;
    return (
      <>
        <div>{toUSD(profitLossValue)}</div>
        <div className="profit-percent fst-12">{pctDisplay}</div>
      </>
    );
  };

  /**
   * Calculates attribution-aware actual cost for a stage hierarchy.
   * For attribution-flagged stages, uses only direct attributed values.
   * For non-flagged stages, sums task actuals + recursively sums child stage actuals.
   * 
   * @param {Object} stageNode - Stage object
   * @param {Array} selectedTask - Array of selected tasks
   * @returns {number} Attribution-aware actual cost
   */
  calculateAttributionAwareActualCost = (stageNode, selectedTask) => {
    if (!stageNode) return 0;

    const sumNumber = (v) => parseFloat(v) || 0;

    // Attribution-flagged stage: use only direct attribution totals (do not include subtree tasks)
    if (stageNode.is_attribution_level) {
      return sumNumber(stageNode.attributedActualMaterialCost) +
        sumNumber(stageNode.attributedActualLaborCost) +
        sumNumber(stageNode.attributedActualSubContractorCost);
    }

    let total = 0;

    // Include tasks directly under this stage (only if selected)
    if (stageNode.tasks && Array.isArray(stageNode.tasks)) {
      stageNode.tasks.forEach((task) => {
        if (selectedTask.findIndex((t) => t.id === task.id) !== -1) {
          total +=
            sumNumber(task.actualMaterialCost) +
            sumNumber(task.actualLaborCost) +
            sumNumber(task.actualSubContractorCost);
        }
      });
    }

    // Include child stages (recursively attribution-aware)
    if (stageNode.stages && Array.isArray(stageNode.stages)) {
      stageNode.stages.forEach((child) => {
        total += this.calculateAttributionAwareActualCost(child, selectedTask);
      });
    }

    return total;
  };

  projectCalculation = (stages, optionData) => {
    let { laborCost, laborCostByStageId, materialActualByStageId, subcontractorActualByStageId, selectedTask, materialCommittedByStageId, subcontractorCommittedByStageId, totalCommittedByStageId, committedByTaskId, taskMaterialCommittedData, taskSubcontractorCommittedData } = this.state;
    let projectSummary = {
      contractedPrice: 0,
      reviedContractedPrice: 0,
      actualCost: 0,
    };

    if (stages && Array.isArray(stages)) {
      const traverseStages = (stages) => {
        for (const stage of stages) {
          // Attribution-flagged section: capture labor actuals attributed directly to this stage (Timesheet.stage_id)
          stage.attributedActualLaborCost = stage?.is_attribution_level
            ? (parseFloat(laborCostByStageId?.[stage.id] || 0) || 0)
            : 0;

          // Attribution-flagged section: capture PO/WO actuals attributed directly to this stage (PurchaseOrder.stage_id)
          stage.attributedActualMaterialCost = stage?.is_attribution_level
            ? (parseFloat(materialActualByStageId?.[stage.id] || 0) || 0)
            : 0;
          stage.attributedActualSubContractorCost = stage?.is_attribution_level
            ? (parseFloat(subcontractorActualByStageId?.[stage.id] || 0) || 0)
            : 0;

          stage.attributedCommittedMaterialCost = stage?.is_attribution_level
            ? (parseFloat(materialCommittedByStageId?.[stage.id] || 0) || 0)
            : 0;
          stage.attributedCommittedSubContractorCost = stage?.is_attribution_level
            ? (parseFloat(subcontractorCommittedByStageId?.[stage.id] || 0) || 0)
            : 0;
          stage.attributedCommittedCost = (parseFloat(totalCommittedByStageId?.[stage.id] || 0) || 0);

          if (Array.isArray(stage.stages)) {
            traverseStages(stage.stages);
          }
          if (stage && Array.isArray(stage.tasks)) {
            stage.tasks.forEach((task) => {
              task.allocatedMaterialCost = parseFloat(task.materialTotal_SaleAmount) || 0;
              task.allocatedLaborCost = parseFloat(task.laborTotal_SaleAmount) || 0;
              task.allocatedSubContractorCost = parseFloat(task.subcontractorTotal_SaleAmount) || 0;
              task.allocatedProfitMarginCost = parseFloat(task.profitMarginCost) || 0;

              const { actualMaterialCost, actualLaborCost, actualSubContractorCost, actualProfitMarginCost } =
                this.calculateActualBudget(task, laborCost);
              task.actualMaterialCost = parseFloat(actualMaterialCost) || 0;
              task.actualLaborCost = parseFloat(actualLaborCost) || 0;
              task.actualSubContractorCost = parseFloat(actualSubContractorCost) || 0;
              task.actualProfitMarginCost = parseFloat(actualProfitMarginCost) || 0;
              task.committedCost = parseFloat(committedByTaskId?.[task.id] || 0) || 0;
              task.committedMaterialCost = parseFloat(taskMaterialCommittedData?.[task.id] || 0) || 0;
              task.committedSubContractorCost = parseFloat(taskSubcontractorCommittedData?.[task.id] || 0) || 0;

              task.calculatedPercentageComplete = parseFloat(task.percentageComplete) || 0;

              if (selectedTask.findIndex((t) => t.id === task.id) !== -1) {
                const taskSaleTotal =
                  (parseFloat(task.materialTotal_SaleAmount) || 0) +
                  (parseFloat(task.laborTotal_SaleAmount) || 0) +
                  (parseFloat(task.subcontractorTotal_SaleAmount) || 0);
                projectSummary.contractedPrice += taskSaleTotal;
                projectSummary.reviedContractedPrice += taskSaleTotal;
              }
            });
          }
        }
      };
      traverseStages(stages);

      // Calculate attribution-aware actual cost for all top-level stages
      stages.forEach((stage) => {
        projectSummary.actualCost += this.calculateAttributionAwareActualCost(stage, selectedTask);
      });
    }

    optionData.forEach((option) => {
      const { actualMaterialCost, actualLaborCost, actualSubContractorCost, actualProfitMarginCost } = this.calculateActualBudget(option, laborCost);
      option.actualMaterialCost = parseFloat(actualMaterialCost) || 0;
      option.actualLaborCost = parseFloat(actualLaborCost) || 0;
      option.actualSubContractorCost = parseFloat(actualSubContractorCost) || 0;

      option.allocatedProfitMarginCost = parseFloat(actualProfitMarginCost) || 0;
      option.committedCost = parseFloat(committedByTaskId?.[option.id] || 0) || 0;
      option.committedMaterialCost = parseFloat(taskMaterialCommittedData?.[option.id] || 0) || 0;
      option.committedSubContractorCost = parseFloat(taskSubcontractorCommittedData?.[option.id] || 0) || 0;

      if (!['REJECTED', 'CANCELLED'].includes(option.proposal_status) && selectedTask.findIndex((t) => t.id === option.id) !== -1) {
        const optionSaleTotal =
          (parseFloat(option.materialTotal_SaleAmount) || 0) +
          (parseFloat(option.laborTotal_SaleAmount) || 0) +
          (parseFloat(option.subcontractorTotal_SaleAmount) || 0);
        projectSummary.reviedContractedPrice += optionSaleTotal;
        // Variations are not attribution-flagged, so include their actuals directly
        projectSummary.actualCost +=
          (parseFloat(option.actualMaterialCost) || 0) +
          (parseFloat(option.actualLaborCost) || 0) +
          (parseFloat(option.actualSubContractorCost) || 0);
      }
    });
    this.setState({ stages, optionData, projectSummary }, () =>
      this.handleClose()
    );
  };

  generateVariationNumber = (n, width, z) => {
    z = z || "0";
    n = n + "";
    return n.length >= width
      ? `V-${n}`
      : `V-${new Array(width - n.length + 1).join(z) + n}`;
  };

  handleCheckboxClick = (task, checked, type) => {
    let { selectedTask, stages, optionData } = this.state;
    if (checked) {
      selectedTask.push({ ...task, type });
    } else {
      const index = selectedTask.findIndex((t) => t.id === task.id);
      selectedTask.splice(index, 1);
    }
    this.setState({ selectedTask }, () =>
      this.projectCalculation(stages, optionData)
    );
  };

  handleEditClick = (
    task,
    type,
    stageId = "",
    taskId = "",
    subTaskId = "",
    optionId = ""
  ) => {
    const { projectId } = this.state;
    const isSubTask = type === "SUBTASK" ? true : false;
    const editSubTask = isSubTask;
    const editTask = !isSubTask && !optionId;
    const isOption = type === "VARIATION" ? true : false;
    const isVariation = type === "VARIATION" ? true : false;
    const editOption = isOption;
    const updatePermission = this.props.permissions.updatePermission;

    this.setState({
      updatePermission: updatePermission,
      taskDialogState: true,
      taskData: { ...task, type },
      stageId,
      taskId,
      isSubTask,
      subTaskId,
      editSubTask,
      isOption,
      editOption,
      optionId,
      editTask,
      projectId: projectId,
      isVariation: isVariation,
    });
  };

  cancelHandler = () => {
    const { projectId } = this.state;
    this.setState({ selectedTask: [] });
    this.props.history(`/${PROJECT_MANAGEMENT}/view/${projectId}`);
  };

  handleClose = () => {
    this.setState({
      taskDialogState: false,
      updatePermission: this.props.permissions.updatePermission,
      taskData: {},
      stageId: "",
      taskId: "",
      budgetRetentionDialog: false,
      subTaskId: "",
      editSubTask: false,
      editTask: false,
      optionId: "",
    });
  };

  getLevels = (levelId) => {
    this.props.getLevel(levelId).then((level) => {
      const labors = [];
      level.hourlyCost &&
        level.hourlyCost.forEach((cost) => {
          labors.push({
            ...cost,
            calloutCost: level.calloutCost,
            travelCost: level.travelCost,
          });
        });
      this.setState({ labors });
    });
  };

  handleBudgetClick = () => {
    const { projectId } = this.state;
    this.props.history(`/${PROJECT_MANAGEMENT}/budget/${projectId}`);
    this.getProject(projectId);
  };

  handleGeneratePC = () => {
    let { selectedTask = [], projectId } = this.state;
    if (!selectedTask.length) {
      this.props.warningSnackBar("Select a Task to Generate Invoice!");
      return;
    }
    this.props.history(`/${PROJECT_MANAGEMENT}/budget/${projectId}/create`);
  };

  ViewProjectList = () => {
    const { projectState } = this.props;
    const projectType = this.state.projectType || new URLSearchParams(this.props.location.search).get("project-type");
    const resolvedType = resolveProjectType(projectType, projectState);
    this.props.history(`/${PROJECT_MANAGEMENT}?project-type=${resolvedType}`);
  }

  ViewProject = () => {
    let { projectId } = this.state;
    this.props.history(`/${PROJECT_MANAGEMENT}/view/${projectId}`);
  }
  TotalClaimAmount = (progressClaims = []) => {
    return progressClaims
      .filter((claim) => claim.progressClaimStatus === 'Approved' || claim.progressClaimStatus === 'Finalised')
      .reduce((total, claim) => total + (claim.claimAmount || 0), 0);
  };

  navigateToCostBreakdown = (costType, taskId, stageId) => {
    const { projectId } = this.state;
    let url = `/${PROJECT_MANAGEMENT}/${PROJECT_BUDGET_COST_BREAKDOWN}`
      .replace(':projectId', projectId)
      .replace(':costType', costType);

    // If stageId is provided, use it; otherwise use taskId
    if (stageId) {
      url = url.replace(':taskId', `stage/${stageId}`);
    } else {
      url = url.replace(':taskId', taskId || '');
    }

    // Use navigate if available (React Router v6), otherwise fall back to history
    if (this.props.navigate) {
      this.props.navigate(url);
    } else if (this.props.history) {
      this.props.history(url);
    } else {
      window.location.href = url;
    }
  };


  render() {
    const {
      stages,
      hasStages = true,
      selectedTask,
      progressClaims,
      taskDialogState,
      updatePermission,
      projectId,
      levelId,
      optionData,
      budgetRetentionDialog,
      projectName,
      customerName,
      stageId,
      taskId,
      // categories,
      // items,
      isSubTask,
      subTaskId,
      labors,
      editSubTask,
      editTask,
      isOption,
      isVariation,
      editOption,
      optionId,
      projectSummary,
      projectType,
      projectTypeStatus,
      projectQuoteType,
      unAllocatedTimesheetTime,
      allocateTimesheetTaskDialog,
      calculations,
      variationsWithoutSection,
      open,
      quoteType,
      projectStatus,
    } = this.state;

    const optionsTotals = calculatVariationsTotalsBudget(variationsWithoutSection, progressClaims);
    const inProgressQuote = quoteType === "quote" && projectStatus === "IN_PROGRESS";
    let currentTask, currentTaskCost;
    if (!isSubTask && !subTaskId && taskId) {
      currentTask = stages.filter((item) => item.id === stageId)
        .map((item) => item.tasks)[0]
        ?.filter((item) => item.id === taskId)[0] || null;
      if (currentTask) {
        currentTaskCost = {
          materialCost: currentTask.materialTotal_SaleAmount,
          laborCost: currentTask.laborTotal_SaleAmount,
          subContractorCost: currentTask.subcontractorTotal_SaleAmount,
          profitMarginCost: currentTask.profitMarginCost,
          total:
            currentTask.materialTotal_SaleAmount +
            currentTask.laborTotal_SaleAmount +
            currentTask.subcontractorTotal_SaleAmount,
        };
        currentTaskCost["noOfItems"] = 0;
        currentTaskCost["noOfResources"] = 0;
        if (idx(currentTask, _ => _.subTasks) && Array.isArray(currentTask.subTasks)) {
          for (let subTask of currentTask.subTasks) {
            currentTaskCost["noOfItems"] += subTask.material.length || 0;
            currentTaskCost["noOfResources"] += subTask.labor.length || 0;
          }
        }
      }
    }

    let lessRetentions = [];
    progressClaims && Array.isArray(progressClaims) && progressClaims.map((claim) => {
      lessRetentions.push({
        progressClaimId: claim.id,
        progressClaimUniqueId: claim.u_id,
        total: claim.totalValue,
        claimAmount: claim.claimAmount,
        retentionAmount: claim.retentionAmount,
      });
    });

    return (
      <Routes>
        <Route
          path={`create`}
          element={
            <PreviewBudget
              selectedTask={selectedTask}
              projectId={projectId}
              progressClaims={progressClaims}
              stages={stages}
              hasStages={hasStages}
              optionData={optionData}
              handleBudgetClick={this.handleBudgetClick}
              handleClose={this.cancelHandler}
            />
          }
        />
        <Route
          path={`cost-breakdown/:costType/:taskId`}
          element={<CostBreakdown projectId={this.props.params?.projectId || this.state.projectId} />}
        />
        {/* Explicit route for stage breakdowns to ensure proper matching */}
        <Route
          path={`cost-breakdown/:costType/stage/:stageId`}
          element={<CostBreakdown projectId={this.props.params?.projectId || this.state.projectId} />}
        />
        <Route
          path=""
          element={
            <>
              {taskDialogState && (isVariation ? <AddVariation key="AddTask"
                projectName={projectName}
                customerName={customerName}
                projectId={projectId}
                open={taskDialogState}
                dialogClose={this.handleClose}
                stageId={stageId}
                taskId={taskId}
                // categories={categories} // these props never used in the component
                // items={items} // these props never used in the component
                isSubTask={isSubTask}
                isOption={isOption}
                isVariation={isVariation}
                optionId={optionId}
                editOption={editOption}
                subTaskId={subTaskId}
                customerLevel={levelId}
                editTask={editTask}
                editSubTask={editSubTask}
                updatePermission={updatePermission || this.props.user.global_user}
                labors={labors}
                currentTask={currentTask}
                currentTaskCost={currentTaskCost}
                getStages={() => {
                  this.getProject(true);
                }}
                getOptions={() => {
                  this.getProject(true);
                }}
                projectType={projectTypeStatus}
                userAccessControl={this.props.userAccessControl} />
                : <AddTask
                  key="AddTask"
                  projectName={projectName}
                  customerName={customerName}
                  projectId={projectId}
                  open={taskDialogState}
                  dialogClose={this.handleClose}
                  stageId={stageId}
                  taskId={taskId}
                  // categories={categories} // these props never used in the component
                  // items={items} // these props never used in the component
                  isSubTask={isSubTask}
                  isOption={isOption}
                  isVariation={isVariation}
                  optionId={optionId}
                  editOption={editOption}
                  subTaskId={subTaskId}
                  customerLevel={levelId}
                  editTask={editTask}
                  editSubTask={editSubTask}
                  updatePermission={updatePermission || this.props.user.global_user}
                  labors={labors}
                  currentTask={currentTask}
                  currentTaskCost={currentTaskCost}
                  getStages={() => {
                    this.getProject(true);
                  }}
                  getOptions={() => {
                    this.getProject(true);
                  }}
                  projectType={projectTypeStatus}
                  userAccessControl={this.props.userAccessControl}
                />)}
              {allocateTimesheetTaskDialog && (
                <AllocateTimesheetTask
                  key="AllocateTimesheetTask"
                  open={allocateTimesheetTaskDialog}
                  selectedTask={selectedTask}
                  projectId={projectId}
                  projectType={projectType}
                  stages={stages}
                  submitData={this.submitData}
                  dialogClose={() =>
                    this.setState({ allocateTimesheetTaskDialog: false })
                  }
                />
              )}
              {budgetRetentionDialog && (
                <BudgetRetention
                  open={budgetRetentionDialog}
                  progressClaims={progressClaims}
                  projectId={projectId}
                  key="BudgetRetention"
                  getProgressClaimList={this.getProgressClaimList}
                  dialogClose={this.handleClose}
                  userAccessControl={this.props.userAccessControl}
                />
              )}

              <div className="purchase-order-block">
                <div className="row">
                  <div className="w-auto">
                    <h2 className="content-heading text-uppercase">Budget</h2>
                    <div className="col-12 breadcrumb-block p-0">
                      <Breadcrumb>
                        <Breadcrumb.Item onClick={this.ViewProjectList}>PROJECTS</Breadcrumb.Item>
                        <Breadcrumb.Item onClick={this.ViewProject}>{projectName}</Breadcrumb.Item>
                        <Breadcrumb.Item active>Budget</Breadcrumb.Item>
                      </Breadcrumb>
                    </div>
                  </div>
                  {/* NOTE: Progress Claim Retention button temporarily commented out - needs to be reformed in future */}
                  {/* {projectQuoteType == "quote" && (<div className="w-auto ms-auto mt-auto">
                    <button
                      className="secondarybtn secondarybtn-outline btn-ch ms-auto btn btn-primary mb-3"
                      onClick={() => this.setState({ budgetRetentionDialog: true })}
                    >
                      Progress Claim Retention
                    </button>
                  </div>)} */}
                </div>
                <div class="table-responsive tender-table">
                  <div className="view-project-page">
                    <div className="overviewContainer">

                      <div className="MuiPaper-root main MuiPaper-elevation1 MuiPaper-rounded">
                        <Card className="border-0">
                          <CardHeader className="sections d-flex justify-content-between align-items-center bg-body card-header">
                            <h6 className="mb-0">SECTIONS</h6>
                            <div className="d-flex gap-2">

                              {/* <h6 className="tbl-action-btn mb-0">
              <ModeEditOutlineOutlinedIcon />EDIT TENDER
            </h6>

          <h6 className="tbl-action-btn mb-0">
            <AddIcon /> ADD SECTION
          </h6> */}
                            </div>
                          </CardHeader>
                          <CardBody className="d-flex flex-column gap-2 p-0 pt-2">
                            {stages && Array.isArray(stages) && stages.length ? (
                              stages.map((stage) => {
                                return (
                                  <StageAccordionBudget
                                    key={stage.id}
                                    stage={stage}
                                    projectQuoteType={projectQuoteType}
                                    calculations={calculations}
                                    variations={optionData}
                                    quoteType={projectQuoteType}
                                    stageId={stage.id}
                                    progressClaims={progressClaims}
                                    handleEditClick={this.handleEditClick}
                                    navigateToCostBreakdown={this.navigateToCostBreakdown}
                                    inProgressQuote={inProgressQuote}
                                    isGlobalUser={this.props.user.global_user}
                                    updatePermission={updatePermission}
                                  />
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan={7} className="text-center">No Data to Show</td>
                              </tr>
                            )}
                          </CardBody>
                        </Card>
                      </div>

                    </div>
                  </div>
                </div>

                {variationsWithoutSection && variationsWithoutSection.length > 0 && (
                  <div className="row">
                    <div className="col-12">
                      <div className="material-list-block mt-4">
                        <div className="table-responsive">
                          <Table
                            bordered
                            className={`table-create table-material custom-budget-table table-material-ch budget-table ${projectQuoteType === "noquote" ? "highlight-noquote" : ""
                              }`}
                            data-cy={'budget-variation-table'}
                          >
                            <thead>
                              <tr>
                                <th>Variation</th>
                                <th>Completion %</th>
                                <th className="text-right">Claimed</th>
                                <th className="text-right">Sale Amount</th>
                                <th className="text-right">Forecast Cost</th>
                                <th className="text-right">Committed Cost</th>
                                <th className="text-right">Actual Cost</th>
                                {projectQuoteType === "quote" && (
                                  <th className="text-right">
                                    Profit (Forecast)
                                  </th>
                                )}
                                <th className="text-right">
                                  Profit (Actual)
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {variationsWithoutSection &&
                                Array.isArray(variationsWithoutSection) &&
                                variationsWithoutSection.length ? (
                                <>
                                  {variationsWithoutSection.map((option, index) => {
                                    const taskTotal = calculateVariationsBudget(option, progressClaims);
                                    const budget = toDecimal(option.actualMaterialCost || 0)
                                      .plus(toDecimal(option.actualLaborCost || 0))
                                      .plus(toDecimal(option.actualSubContractorCost || 0))
                                      .toNumber();
                                    const claimAmount = taskTotal.taskTotalClaimAmount || 0;
                                    const saleAmount = taskTotal.total || 0;
                                    const committedCost = option.committedCost || 0;
                                    let completionPercentage = 0.0; // Default value
                                    if (saleAmount > 0) completionPercentage = (claimAmount / saleAmount) * 100;
                                    return (
                                      <Fragment key={option.id}>
                                        <tr>
                                          <td>
                                            <p className="m-0 budget-task">
                                              <span>{`${option.variationNo} ${option.name}`}
                                                {option.revisionNumber !== 1 ? " - " + option.revisionName : ""}
                                                <span className="task-edit-icon">
                                                  <VisibilityIcon className='view-icon' onClick={() => this.handleEditClick(option, "VARIATION", "", "", "", option.id)} />
                                                </span>
                                              </span>
                                              <span className="expand-icon">
                                                <ExpandMoreIcon onClick={() =>
                                                  this.setState({
                                                    open: {
                                                      ...open,
                                                      [option.id]: !open[option.id],
                                                    },
                                                  })
                                                }
                                                  aria-controls="example-collapse-text"
                                                  aria-expanded={open[option.id]}
                                                />
                                              </span>
                                            </p>
                                          </td>
                                          <td data-cy='completion-percentage'>{roundValue(completionPercentage, 0)}%</td>
                                          <td className="text-right" data-cy='claim-amount'>{toUSD(claimAmount)}</td>
                                          <td className="text-right" data-cy='sale-amount'>{toUSD(saleAmount)}</td>
                                          <td className="text-right" data-cy='forecast-cost'>{toUSD(taskTotal.subtotal)}</td>
                                          <td className="text-right" data-cy='committed-cost'>{toUSD(committedCost)}</td>
                                          <td className="text-right" data-cy='actual-cost'>
                                            <span
                                              className="actual-cost-link"
                                              onClick={() => this.navigateToCostBreakdown('all', option.id)}
                                            >
                                              {toUSD(budget)}
                                            </span>
                                          </td>
                                          {projectQuoteType === "quote" && (<td className="text-right" data-cy='gross-profit-forecast'>{toUSD(taskTotal.total_profit)}</td>)}
                                          <td className={`text-right ${claimAmount - budget >= 0 ? claimAmount - budget > 0 ? "budget-under" : "" : "budget-exceed"}`} data-cy='gross-profit-actual'>
                                            {toUSD(getClaimProfitAmount(claimAmount, budget))}
                                          </td>
                                        </tr>
                                        {/* Collapse section with breakdown */}
                                        <tr>
                                          <td colSpan={projectQuoteType === "quote" ? 9 : 8} className="p-0 border-0">
                                            <Collapse in={open[option.id]}>
                                              <div id="example-collapse-text">
                                                <Table bordered className="table-create table-material table-material-ch" data-cy={'budget-variation-table-content'}>
                                                  <thead>
                                                    <tr>
                                                      <th></th>
                                                      <th></th>
                                                      <th className="text-right"></th>
                                                      <th className="text-right"></th>
                                                      <th className="text-right"></th>
                                                      <th className="text-right"></th>
                                                      <th className="text-right"></th>
                                                      {projectQuoteType === "quote" && (<th className="text-right"></th>)}
                                                      <th className="text-right"></th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    <tr>
                                                      <td className="p-0"></td>
                                                      <td className="text-right"></td>
                                                      <td className="collapse-title text-right"><span>Material</span></td>
                                                      <td className="text-right">{toUSD(taskTotal.material.sale)}</td>
                                                      <td className="text-right">{toUSD(taskTotal.material.cost)}</td>
                                                      <td className="text-right">{toUSD(option.committedMaterialCost || 0)}</td>
                                                      <td className="text-right">
                                                        <span
                                                          className="actual-cost-link"
                                                          onClick={() => this.navigateToCostBreakdown('material', option.id)}
                                                        >
                                                          {toUSD(option.actualMaterialCost || 0)}
                                                        </span>
                                                      </td>
                                                      {projectQuoteType === "quote" && (<td className="text-right">{toUSD(taskTotal.material.profit)}</td>)}
                                                      <td className="text-right">{toUSD(taskTotal.material.sale - (option.actualMaterialCost || 0))}</td>
                                                    </tr>
                                                    <tr>
                                                      <td className="p-0"></td>
                                                      <td className="text-right"></td>
                                                      <td className="collapse-title text-right"><span>Resource</span></td>
                                                      <td className="text-right">{toUSD(taskTotal.labor.sale)}</td>
                                                      <td className="text-right">{toUSD(taskTotal.labor.cost)}</td>
                                                      <td className="text-right">{toUSD(0)}</td>
                                                      <td className="text-right">
                                                        <span
                                                          className="actual-cost-link"
                                                          onClick={() => this.navigateToCostBreakdown('labor', option.id)}
                                                        >
                                                          {toUSD(option.actualLaborCost || 0)}
                                                        </span>
                                                      </td>
                                                      {projectQuoteType === "quote" && (<td className="text-right">{toUSD(taskTotal.labor.profit)}</td>)}
                                                      <td className="text-right">{toUSD(taskTotal.labor.sale - (option.actualLaborCost || 0))}</td>
                                                    </tr>
                                                    <tr>
                                                      <td className="p-0"></td>
                                                      <td className="text-right"></td>
                                                      <td className="collapse-title text-right"><span>Contractor</span></td>
                                                      <td className="text-right">{toUSD(taskTotal.subcontractor.sale)}</td>
                                                      <td className="text-right">{toUSD(taskTotal.subcontractor.cost)}</td>
                                                      <td className="text-right">{toUSD(option.committedSubContractorCost || 0)}</td>
                                                      <td className="text-right">
                                                        <span
                                                          className="actual-cost-link"
                                                          onClick={() => this.navigateToCostBreakdown('subcontractor', option.id)}
                                                        >
                                                          {toUSD(option.actualSubContractorCost || 0)}
                                                        </span>
                                                      </td>
                                                      {projectQuoteType === "quote" && (<td className="text-right">{toUSD(taskTotal.subcontractor.profit)}</td>)}
                                                      <td className="text-right">{toUSD(taskTotal.subcontractor.sale - (option.actualSubContractorCost || 0))}</td>
                                                    </tr>
                                                  </tbody>
                                                </Table>
                                              </div>
                                            </Collapse>
                                          </td>
                                        </tr>
                                      </Fragment>
                                    );
                                  })}
                                  <tr className="budget-total">
                                    <td></td>
                                    <td><b>Total</b></td>
                                    <td className="text-right">{toUSD(optionsTotals.claim_total)}</td>
                                    <td className="text-right">{toUSD(optionsTotals.total)}</td>
                                    <td className="text-right">{toUSD(optionsTotals.subtotal)}</td>
                                    <td className="text-right">{toUSD(optionsTotals.committedCost || 0)}</td>
                                    <td className="text-right">{toUSD(optionsTotals.budget)}</td>
                                    {projectQuoteType === "quote" && (
                                      <td className="text-right">
                                        <div>{toUSD(optionsTotals.total_profit)}</div>
                                        <div className="profit-percent fst-12">({toPercent(optionsTotals.total_profit, optionsTotals.budget)})</div>
                                      </td>
                                    )}
                                    <td className="text-right">
                                      <div>{toUSD(getClaimProfitAmount(optionsTotals.claim_total, optionsTotals.budget))}</div>
                                      <div className="profit-percent fst-12">{getClaimProfitPct(optionsTotals.claim_total, optionsTotals.budget)}</div>
                                    </td>
                                  </tr>
                                </>
                              ) : (
                                <tr>
                                  <td colSpan={projectQuoteType === "quote" ? 9 : 8} className="text-center">No Data to Show</td>
                                </tr>
                              )}

                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <ProgressClaimRetention
                  headerName="Progress Claims"
                  progressClaims={progressClaims}
                  projectQuoteType={projectQuoteType}
                />

                <ReceiveCreditInvoiceBudget
                  headerName="Receive Credit Invoice"
                  creditTotal={this.state.projectCreditTotal || (idx(this.props.projectSummary, _ => _.project.res.creditInvoiceTotal) || 0)}
                  actualCostToDate={(projectSummary.actualCost || 0) + (unAllocatedTimesheetTime || 0)}
                  show={true}
                />

                {this.props.entitlements.scheduleMenu && unAllocatedTimesheetTime ? (
                  <div className="row mt-2 table-responsive">
                    <div className="col-4">
                      Unallocated Resource
                    </div>
                    <div className="col-4">
                      {numberFormat(unAllocatedTimesheetTime)}
                    </div>
                    <div className="col-4">
                      <button
                        className="secondarybtn secondarybtn-outline btn-ch btn btn-primary"
                        data-cy="resource-allocate"
                        onClick={() => this.setState({ allocateTimesheetTaskDialog: true, })}
                      >
                        Allocate
                      </button>
                    </div>
                  </div>
                ) : ""}
                <div className="col-12 total-table mt-3 table-responsive">
                  <table className="table-create mb-0 tbl-fixed tbl-fixed-width table table-bordered">
                    <thead>
                      <tr>
                        <th>Original Contract</th>
                        <th>Revised Contract Amount</th>
                        <th>Total Claim Amount</th>
                        <th>Actual Cost To Date</th>
                        <th>Profit/Loss</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <span className="tableinfo">{toUSD(projectSummary.contractedPrice || 0)}</span>
                        </td>
                        <td>
                          <span className="tableinfo">{toUSD(projectSummary.reviedContractedPrice || 0)}</span>
                        </td>
                        <td>
                          <span className="tableinfo">{toUSD(this.TotalClaimAmount(progressClaims) || 0)}</span>
                        </td>
                        <td>
                          <span className="tableinfo">
                            {toUSD((projectSummary.actualCost || 0) + (unAllocatedTimesheetTime || 0))}
                          </span>
                        </td>
                        <td>
                          <span className="tableinfo">
                            {this.getProfitLossDisplay(
                              this.TotalClaimAmount(progressClaims) || 0,
                              projectSummary.actualCost || 0,
                              unAllocatedTimesheetTime || 0
                            )}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-12 d-flex justify-content-end gap-3 mt-5">
                  {this.props.wFinancePermission && (<Button
                    onClick={this.handleGeneratePC}
                    className="secondarybtn"
                  >
                    Generate Invoice
                  </Button>)}
                  <Button onClick={this.cancelHandler} className="secondarybtn cancelbtn">CANCEL</Button>
                </div>
              </div>

            </>
          }
        />
      </Routes>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.user.userData,
  entitlements: state.user.entitlements,
  projectSummary: state.projectSummary,
  projectState: state.project
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  warningSnackBar: bindActionCreators(warningSnackBar, dispatch),
  getProject: bindActionCreators(getProject, dispatch),
  getOptions: bindActionCreators(getOptions, dispatch),
  getProgressClaimList: bindActionCreators(getProgressClaimList, dispatch),
  getCategories: bindActionCreators(getCategories, dispatch),
  getItems: bindActionCreators(getItems, dispatch),
  getLevel: bindActionCreators(getLevel, dispatch),
  getLaborCost: bindActionCreators(getLaborCost, dispatch),
  getStageAttributedPoActuals: bindActionCreators(getStageAttributedPoActuals, dispatch),
  getProjectSummary: (data) => dispatch(getProjectSummary(data)),
  calculateActualBudget: (data) => dispatch(calculateActualBudgetData(data)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CreateBudget)
);
